"use client";

import { useState } from "react";
import { Button } from "../../../components/ui/Button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import QuickPaymentPanel from "../../../components/QuickPaymentPanel";
import PosPaymentStep from "../../../components/PosPaymentStep";
import { useAuth } from "../../../auth/AuthContext";

export default function DailySalesPage() {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const quickPaymentType = (user?.company?.quick_payment_type as "direct" | "stylist") || "stylist";
  const [date] = useState("25th June 2025");
  const [showQuickPaymentPanel, setShowQuickPaymentPanel] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"quick" | "paynow" | null>(
    null
  );

  const leftSummary = [
    { label: "Services", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "Products", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "Shipping", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "Gift cards", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "Memberships", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "Late cancellation fees", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "No-show fees", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
    { label: "Refund amount", sales: 2, refund: 2, total: `${currencySymbol}0.00` },
  ];

  const rightSummary = [
    { label: "Cash", payments: `${currencySymbol}0.00`, refunds: `${currencySymbol}0.00` },
    { label: "Other", payments: `${currencySymbol}0.00`, refunds: `${currencySymbol}0.00` },
    { label: "Gift card redemptions", payments: `${currencySymbol}0.00`, refunds: `${currencySymbol}0.00` },
    { label: "Payments collected", payments: `${currencySymbol}0.00`, refunds: `${currencySymbol}0.00` },
    { label: "Of which tips", payments: `${currencySymbol}0.00`, refunds: `${currencySymbol}0.00` },
    { label: "Late cancellation fees", payments: `${currencySymbol}0.00`, refunds: `${currencySymbol}0.00` },
  ];

  return (
    <div className="p-4 lg:p-6">
      {/* Header Row */}
      <div className="bg-white border-b border-gray-200 -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 px-4 lg:px-6 py-4 lg:py-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
        <h1 className="text-xl lg:text-[22px] font-bold text-[#885ABB]">{date}</h1>
        <Button
          onClick={() => setShowQuickPaymentPanel(true)}
          size="lg"
          className="w-full sm:w-auto bg-[#22C55E] hover:bg-[#16a34a] text-white rounded-xl font-bold flex items-center justify-center gap-2 transition h-11 lg:h-12"
        >
          <img src="/quick.svg" alt="Quick Payment" className="w-5 h-5 lg:w-6 lg:h-6" />
          <span>Quick Payment</span>
        </Button>
      </div>

      {/* Navigation Row - Scrollable on mobile */}
      <div className="mb-6 relative">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
          {/* Left controls */}
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-10 px-2 lg:px-3 border-purple-200 text-gray-700 text-xs lg:text-[16px] font-medium whitespace-nowrap"
            >
              <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5 text-[#885ABB]" strokeWidth={3} />
              <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5 -ml-2.5 lg:-ml-3 text-[#885ABB]" strokeWidth={3} />
              <span className="ml-1">Week</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 px-2 lg:px-3 border-purple-200 text-gray-700 text-xs lg:text-[16px] font-medium whitespace-nowrap"
            >
              <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5 text-[#885ABB]" strokeWidth={3} />
              <span className="ml-1">Day</span>
            </Button>
          </div>

          {/* Middle group */}
          <div className="flex border border-purple-200 rounded-lg overflow-hidden bg-white shrink-0">
            {["Today", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
              (day, idx) => (
                <Button
                  key={day}
                  variant="ghost"
                  size="sm"
                  className={`h-10 px-4 text-gray-700 rounded-none text-xs lg:text-[16px] font-medium whitespace-nowrap
                    ${idx !== 0 ? "border-l border-purple-100" : ""}
                    hover:bg-purple-50 hover:text-[#885ABB]`}
                >
                  {day}
                </Button>
              )
            )}
          </div>

          {/* Right controls */}
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-10 px-2 lg:px-3 border-purple-200 text-gray-700 text-xs lg:text-[16px] font-medium whitespace-nowrap"
            >
              <span className="mr-1">Day</span>
              <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5 text-[#885ABB]" strokeWidth={3} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 px-2 lg:px-3 border-purple-200 text-gray-700 text-xs lg:text-[16px] font-medium whitespace-nowrap"
            >
              <span className="mr-1">Week</span>
              <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5 text-[#885ABB]" strokeWidth={3} />
              <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5 -ml-2.5 lg:-ml-3 text-[#885ABB]" strokeWidth={3} />
            </Button>
          </div>
        </div>
        {/* Right side fade for scroll indication */}
        <div className="lg:hidden absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-gray-50/50 to-transparent pointer-events-none" />
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left Transaction Summary */}
        <div className="border border-gray-200 rounded-xl bg-white shadow-sm">
          <div className="px-4 py-3 shadow-sm">
            <h2 className="font-semibold text-[20px] text-gray-900">
              Transaction summary
            </h2>
          </div>

          <div className="mb-3 h-[2px] bg-gray-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)]"></div>

          {/* Table */}
          <div className="divide-y divide-gray-100">
            {/* Column headings */}
            <div className="hidden sm:grid grid-cols-4 px-4 py-4 font-bold text-[13px] text-gray-700 bg-gray-50/50">
              <span>Item type</span>
              <span className="text-center">Sales qty</span>
              <span className="text-center">Refund qty</span>
              <span className="text-right">Gross total</span>
            </div>

            {/* Data rows */}
            {leftSummary.map((row) => (
              <div
                key={row.label}
                className="flex flex-col sm:grid sm:grid-cols-4 px-4 py-4 sm:py-5 text-[13px] text-gray-500 hover:bg-gray-50/50 transition-colors gap-2 sm:gap-0"
              >
                <span className="font-bold sm:font-medium text-gray-900 sm:text-gray-500">{row.label}</span>
                <div className="flex justify-between sm:block sm:text-center mt-1 sm:mt-0">
                  <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Sales Qty</span>
                  <span>{row.sales}</span>
                </div>
                <div className="flex justify-between sm:block sm:text-center">
                  <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Refund Qty</span>
                  <span>{row.refund}</span>
                </div>
                <div className="flex justify-between sm:block sm:text-right pt-2 sm:pt-0 border-t sm:border-0 border-gray-50">
                  <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Gross Total</span>
                  <span className="font-black sm:font-normal text-[#885ABB] sm:text-gray-500">{row.total}</span>
                </div>
              </div>
            ))}

            {/* Totals row */}
            <div className="flex flex-col sm:grid sm:grid-cols-4 px-4 py-5 font-black sm:font-bold text-[14px] text-white sm:text-gray-900 bg-[#402B69] sm:bg-gray-50 gap-2 sm:gap-0">
              <span className="uppercase sm:normal-case tracking-tight sm:tracking-normal">Total</span>
              <div className="flex justify-between sm:block sm:text-center">
                <span className="sm:hidden text-[10px] font-bold text-purple-200 uppercase">Sales Qty</span>
                <span>{leftSummary.reduce((acc, r) => acc + r.sales, 0)}</span>
              </div>
              <div className="flex justify-between sm:block sm:text-center">
                <span className="sm:hidden text-[10px] font-bold text-purple-200 uppercase">Refund Qty</span>
                <span>{leftSummary.reduce((acc, r) => acc + r.refund, 0)}</span>
              </div>
              <div className="flex justify-between sm:block sm:text-right pt-2 sm:pt-0 border-t sm:border-0 border-purple-800">
                <span className="sm:hidden text-[10px] font-bold text-purple-200 uppercase">Gross Total</span>
                <span className="text-lg sm:text-[14px]">{currencySymbol}0.00</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Transaction Summary */}
        <div className="border border-gray-200 rounded-xl bg-white shadow-sm">
          {/* Header */}
          <div className="px-4 py-3 shadow-sm">
            <h2 className="font-semibold text-[20px] text-gray-900">
              Transaction summary
            </h2>
          </div>

          <div className="mb-3 h-[2px] bg-gray-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)]"></div>

          {/* Table */}
          <div className="divide-y divide-gray-100">
            {/* Column headings */}
            <div className="hidden sm:grid grid-cols-3 px-4 py-5 font-bold text-[13px] text-gray-700 bg-gray-50/50">
              <span>Payment type</span>
              <span className="text-center">Payments</span>
              <span className="text-right">Refunds</span>
            </div>

            {/* Data rows */}
            {rightSummary.map((row) => (
              <div
                key={row.label}
                className="flex flex-col sm:grid sm:grid-cols-3 px-4 py-4 text-[13px] text-gray-500 hover:bg-gray-50/50 transition-colors gap-2 sm:gap-0"
              >
                <span className="font-bold sm:font-medium text-gray-900 sm:text-gray-500">{row.label}</span>
                <div className="flex justify-between sm:block sm:text-center">
                  <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Payments</span>
                  <span>{row.payments}</span>
                </div>
                <div className="flex justify-between sm:block sm:text-right pt-2 sm:pt-0 border-t sm:border-0 border-gray-50">
                  <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Refunds</span>
                  <span className="font-bold sm:font-normal text-red-500 sm:text-gray-500">{row.refunds}</span>
                </div>
              </div>
            ))}

            {/* Bold footers as separate rows for mobile */}
            <div className="flex flex-col sm:grid sm:grid-cols-3 px-4 py-4 font-black sm:font-bold text-[14px] text-white sm:text-gray-900 bg-[#402B69] sm:bg-gray-50 gap-2 sm:gap-0 mt-2 sm:mt-0">
              <span className="uppercase sm:normal-case tracking-tight">Total Collected</span>
              <div className="flex justify-between sm:block sm:text-center">
                <span className="sm:hidden text-[10px] font-bold text-purple-200 uppercase">Payments</span>
                <span>{currencySymbol}0.00</span>
              </div>
              <div className="flex justify-between sm:block sm:text-right pt-2 sm:pt-0 border-t sm:border-0 border-purple-800">
                <span className="sm:hidden text-[10px] font-bold text-purple-200 uppercase">Refunds</span>
                <span className="text-lg sm:text-[14px]">{currencySymbol}0.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showQuickPaymentPanel && (
        <QuickPaymentPanel
          onClose={() => setShowQuickPaymentPanel(false)}
          onProceed={() => {
            setShowQuickPaymentPanel(false);
            setSidebarMode("quick");
          }}
          quickPaymentType={quickPaymentType}
        />
      )}

      {sidebarMode && (
        <PosPaymentStep
          selectedService={{
            id: "1",
            name: "Quick Service",
            duration: "30 min",
            price: `${currencySymbol}35`,
          }}
          selectedCustomer="Walk-in"
          appointmentTime={new Date().toISOString()}
          tip={0}
          setTip={() => { }}
          goToFinalPayment={() => setSidebarMode(null)}
          onClose={() => setSidebarMode(null)}
          mode={sidebarMode}
        />
      )}
    </div>
  );
}
